import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { showMessage } from 'app/store/core/messageSlice';
import { useRequest } from './useRequest/useRequest';

/**
 * Devuelve la configuración por defecto para la descarga de archivos tipo Blob.
 *
 * @returns {Object} Opciones por defecto de descarga
 * @property {string} name - Nombre base del archivo a descargar (sin extensión).
 * @property {string|undefined} type - Clave del formato seleccionada dentro de `catalogFormats`.
 * @property {boolean} externalArgs - Indica si se deben usar argumentos externos en lugar de los parámetros por defecto.
 * @property {any} extraData - Información adicional opcional que será enviada en la petición.
 */
const getDefaults = () => ({
  name: 'document',
  type: undefined,
  externalArgs: false,
  extraData: undefined,
});

/**
 * Hook personalizado para descargar archivos como Blob con control de progreso,
 * manejo de errores y soporte para múltiples formatos.
 *
 * @param {Object<string, Object>} catalogFormats
 * Catálogo de formatos disponibles, indexado por clave.
 *
 * Cada formato debe contener:
 * @property {string} label - Etiqueta descriptiva.
 * @property {string} extension - Extensión del archivo (ej. "pdf").
 * @property {string} format - Tipo MIME.
 * @property {string} symbol - Identificador del formato usado por el backend.
 *
 * @param {Function} catalogFunction
 * Función que ejecuta la petición al backend.
 * Debe retornar una Promise compatible con `useRequest`.
 *
 * @param {Object} requestOptions
 * Opciones que se heredan directamente por `useRequest`.
 *
 * @returns {Object} API pública del hook
 *
 * @returns {Array<{label: string, value: string}>} returns.formats
 * Lista de formatos lista para UI.
 *
 * @returns {(entryData: {
 *   type: string,
 *   name?: string,
 *   externalArgs?: boolean,
 *   extraData?: any
 * }, ...args: any[]) => Promise<void>} returns.tryDownload
 * Inicia la descarga de un archivo.
 *
 * @example
 * const { tryDownload } = useDownloadBlob(catalogFormats, exportFile);
 *
 * // Ejemplo con datos por defecto y extras en cuerpo de peticion
 * tryDownload({
 *   type: 'pdf',
 *   name: 'reporte_ventas',
 *   extraData: { filters: { active: true } }
 * });
 *
 * @example
 * // Ejemplo con argumentos externos:
 * const pagination = 10;
 *
 * tryDownload(
 *   { type: 'csv', externalArgs: true },
 *   { format: 'CSV', filters: { active: true } },
 *   pagination,
 * );
 *
 * @returns {number} returns.progress
 * Progreso de descarga (0–100).
 *
 * @returns {boolean} returns.isLoading
 * Indica si la descarga está en curso.
 */
export const useDownloadBlob = (catalogFormats, catalogFunction, requestOptions) => {
  const Dispatch = useDispatch();

  const [progress, setProgress] = useState(0);
  const [formats, setCatalogFormats] = useState([]);

  const { makeRequest: tryExportFile, isLoading } = useRequest(catalogFunction, requestOptions);

  const onDownloadProgress = ({ loaded, total }) => {
    if (total === undefined) {
      return;
    }
    // eslint-disable-next-line no-bitwise
    const fetchingPercent = ((loaded * 100) / total) | 0;
    requestAnimationFrame(() => {
      setProgress(fetchingPercent);
    });
  };

  const handleTransformCatalog = (catalogObj) => {
    const transformedCatalog = Object.keys(catalogObj).map((key) => {
      if (catalogObj[key].label === undefined) {
        throw new Error(`Item ${key} has no prop named 'label'`);
      }
      if (catalogObj[key].extension === undefined) {
        throw new Error(`Item ${key} has no prop named 'extension'`);
      }
      if (catalogObj[key].format === undefined) {
        throw new Error(`Item ${key} has no prop named 'format'`);
      }
      if (catalogObj[key].symbol === undefined) {
        throw new Error(`Item ${key} has no prop named 'symbol'`);
      }
      return {
        label: catalogObj[key].label,
        value: catalogObj[key].extension,
      };
    });
    setCatalogFormats(transformedCatalog);
  };

  useEffect(() => {
    if (
      catalogFormats === undefined ||
      typeof catalogFormats !== 'object' ||
      _.isEmpty(catalogFormats)
    )
      return;
    handleTransformCatalog(catalogFormats);
  }, [catalogFormats]);

  /**
   * Inicia el proceso de descarga del archivo.
   *
   * NOTA: Este JSDoc no es visible externamente.
   * La documentación pública está definida en el `@returns` del hook.
   */
  const tryDownload = async (entryData = {}, ...args) => {
    if (entryData.type === undefined || !entryData || typeof entryData !== 'object') {
      throw new Error(`'type' option was not found in function`);
    }
    const options = _.merge({}, getDefaults(), entryData);
    const defaultReqParams = {
      ...(options.extraData ? { ...options.extraData } : {}),
      format: catalogFormats[options.type].symbol,
    };
    const reqParams = [
      ...(options.externalArgs ? args : [defaultReqParams]),
      { onDownloadProgress },
    ];

    return tryExportFile(...reqParams)
      .then(({ isOk, successContent, messages }) => {
        console.debug(isOk, successContent);
        if (!isOk) {
          Dispatch(
            showMessage({
              message: !Array.isArray(messages)
                ? 'Ocurrió un error al intentar descargar el archivo'
                : messages.join(' '),
              variant: 'primary',
            })
          );
          throw new Error('Failed to obtain file from server');
        } else {
          const url = window.URL.createObjectURL(
            new Blob([successContent], { type: catalogFormats[options.type].format })
          );
          const link = document.createElement('a');
          link.style.display = 'none';
          link.href = url;
          const fileName = `${options.name}.${catalogFormats[options.type].extension}`;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setProgress(0);
      });
  };

  useEffect(() => {
    return () => {
      setProgress(0);
    };
  }, []);

  return {
    /**
     * An array of available formats to download.
     * @type {Array<Object>}
     * @property {string} label - The display label of the format.
     * @property {string} value - The file extension of the format.
     */
    formats,

    /**
     * Inicia el proceso de descarga del archivo.
     *
     * NOTA: Este JSDoc no es visible externamente.
     * La documentación pública está definida en el `@returns` del hook.
     */
    tryDownload,

    /**
     * Current download progress as a percentage.
     * @type {number}
     */
    progress,

    /**
     * Indicates whether the download is currently in progress.
     * @type {boolean}
     */
    isLoading,
  };
};
